import React, { useContext, useEffect, useState } from 'react';
import classes from './OpportunityPage.module.scss';
import Link from '@/helpers/Link';
import ResourceIcon from '@/components/Resources/ResourceIcon/ResourceIcon';
import { VerifyInfo } from '@/components/Resources/ResourceCard/ResourceCardPanels';
import OpenNow from '@/components/Resources/OpenNow/OpenNow';
import VirtualAvailableBadge from '@/components/Resources/ResourceCard/ResourceCardPanels/VirtualAvailableBadge';
import DistanceTo from '@/components/Resources/DistanceTo/DistanceTo';
import ResourceTag from '@/components/Resources/ResourceTag/ResourceTag';
import CLRIcon from '@/components/CLRIcon/CLRIcon';
import StarRating from '@/components/Resources/StarRating/StarRating';
import { ServiceUtilization } from '@/components/Resources/ResourceCard/ResourceCardPanels/ServiceUtilization';
import Resources from '@/utils/api/odas/resource';
import { AccountContext } from '@/context/account.context';
import ReferralNetwork from '@/utils/referral_network';
import { ratingHelpers, resourceHelpers } from '@/utils/helpers';

const OverviewSection = ({ resource, reviewCount, showRating = false, showUtilization = false }) => {
  const account = useContext(AccountContext);
  const hasLocations = resource?.locations?.length > 0;
  const hasRating = showRating && resource?.rating > ratingHelpers.EMPTY_RATING;
  const [utilizationCount, setUtilizationCount] = useState(null);
  const [orgNetwork, setOrgNetwork] = useState(false);
  const [resourceNetworkIds, setResourceNetworkIds] = useState([]);

  useEffect(() => {
    if (!showUtilization || !resource?.id) return;
    Resources.getUtilizationByResource(resource.id).then((response) => {
      if (response?.data?.[resource.id]) {
        setUtilizationCount(response.data[resource.id]);
      }
    });
  }, [showUtilization, resource?.id]);

  // Show a CLR badge for each referral network the resource's org belongs to -- only for
  // CLR pro members. Same pattern as ResourceCard (via resourceHelpers.checkResourceNetworkStatus).
  // Depend on the affiliation and resource id rather than the objects themselves so the
  // network check doesn't refire every time the account object is recreated.
  useEffect(() => {
    if (account.data?.affiliation?.organizationReferralNetworks && resource) {
      resourceHelpers.checkResourceNetworkStatus(resource).then((matchingNetworks) => {
        if (matchingNetworks) {
          setOrgNetwork(ReferralNetwork.inspectAffiliation(account.data));
          setResourceNetworkIds(matchingNetworks.map((n) => n.id));
        }
      });
    }
  }, [account.data?.affiliation, resource?.id]);

  return (
    <div className={classes.overviewSection} id="opportunity-overview">
      <div className={classes.overviewSectionPanel}>
        <div className={classes.overviewSectionHeader}>
          <ResourceIcon resource={resource} className={classes.overviewSectionIcon} boxed circle />
          <div className={classes.overviewSectionTitleWrapper}>
            <h1 className={classes.overviewSectionTitle}>{resource?.title}</h1>
            {resource?.organization?.slug ? (
              <Link href={`/org/${resource.organization.slug}`} className={classes.overviewSectionOrgName}>
                {resource.organization.name}
              </Link>
            ) : (
              <div className={classes.overviewSectionOrgName}>{resource?.organization?.name}</div>
            )}
          </div>
        </div>
        <div className={classes.overviewSectionContent}>
          {resourceNetworkIds.length > 0 && (
            <div className={classes.clrBadgeRow}>
              {resourceNetworkIds.map((id) => (
                <CLRIcon key={id} orgNetwork={orgNetwork} orgNetworkId={id} />
              ))}
            </div>
          )}
          <div className={classes.badgeRow}>
            <VerifyInfo resource={resource} iconOnly={false} />
            {hasLocations && <OpenNow location={resource.locations[0]} />}
            <VirtualAvailableBadge resource={resource} />
          </div>
          {hasLocations && <DistanceTo className={classes.distanceTo} locationList={resource.locations} showIcon={false} showAddress={true} />}
          {(hasRating || (showUtilization && utilizationCount)) && (
            <div className={classes.ratingRow}>
              {hasRating && <StarRating stars={resource.rating} />}
              {hasRating && reviewCount !== null && (
                <a href="#org-review" className={classes.reviewCountLink}>
                  ({reviewCount})
                </a>
              )}
              {showUtilization && <ServiceUtilization count={utilizationCount} />}
            </div>
          )}
          <hr />
          <p className={classes.overviewSectionDesc}>{resource?.description}</p>
          {resource?.tags?.length > 0 && (
            <div className={classes.tagList}>
              {resource.tags.map((tag, index) => (
                <ResourceTag key={index} tag={tag} className={classes.tagBadge} includeTagIcon={false} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewSection;
